// src/QuoteNotes.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Bell, Check, Clock, Trash2, StickyNote, Mail, Info } from 'lucide-react';
import { sendReminderEmail } from './emailService';

const fmtDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const today = () => new Date().toISOString().split('T')[0];
const isOverdue = (remindAt) => remindAt < today();
const isDueToday = (remindAt) => remindAt === today();

// ── Composant Tooltip ─────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <span style={{
          position: 'absolute', bottom: '125%', left: '50%', transform: 'translateX(-50%)',
          background: '#1f2937', color: 'white', fontSize: '11px', borderRadius: '6px',
          padding: '6px 10px', whiteSpace: 'pre-wrap', maxWidth: '220px', textAlign: 'center',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', lineHeight: '1.4',
          pointerEvents: 'none',
        }}>
          {text}
          <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            borderWidth: '4px', borderStyle: 'solid',
            borderColor: '#1f2937 transparent transparent transparent' }} />
        </span>
      )}
    </span>
  );
};

const QuoteNotes = ({ quote, onClose, onReminderChange }) => {
  const { user, profile } = useAuth();
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderMsg, setNewReminderMsg] = useState('');
  const [tab, setTab] = useState('notes');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(null);
  const [previewModal, setPreviewModal] = useState(null); // { reminderId, subject, body, to }

  const clientName = quote.client_info?.clientName || quote.client_info?.name || '—';
  const clientPrenom = clientName.split(' ')[0] || clientName;

  // ── Calcul jours depuis le devis ──────────────────────────────────────────
  const joursDepuisDevis = () => {
    if (!quote.date) return null;
    const devisDate = new Date(quote.date);
    const now = new Date();
    return Math.floor((now - devisDate) / (1000 * 60 * 60 * 24));
  };

  // ── Modèle le plus adapté selon les jours écoulés ─────────────────────────
  const getMeilleureRelance = () => {
    if (!templates.length) return null;
    const jours = joursDepuisDevis();
    if (jours === null) return null;
    const delais = [2, 7, 30, 60, 90];
    // Trouver le délai le plus proche sans dépasser
    let meilleur = null;
    for (const d of delais) {
      if (jours >= d) meilleur = d;
    }
    if (!meilleur) meilleur = 2; // Par défaut J+2
    return templates.find(t => t.delai_jours === meilleur) || templates[0];
  };

  // ── Remplacement des variables dans le modèle ─────────────────────────────
  const appliquerTemplate = (template) => {
    if (!template) return '';
    return template.corps
      .replace(/\[Prenom\]/g, clientPrenom)
      .replace(/\[NumeroDevis\]/g, quote.quote_number || '')
      .replace(/\[Commerciale\]/g, profile?.nom || 'Takoussane Energy');
  };

  const appliquerSujet = (template) => {
    if (!template) return '';
    return template.sujet
      .replace(/\[Prenom\]/g, clientPrenom)
      .replace(/\[NumeroDevis\]/g, quote.quote_number || '')
      .replace(/\[Commerciale\]/g, profile?.nom || 'Takoussane Energy');
  };

  // ── Chargement ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: notesData }, { data: remindersData }, { data: templatesData }] = await Promise.all([
      supabase.from('quote_notes').select('*, profiles(nom)').eq('quote_id', quote.id).order('created_at', { ascending: false }),
      supabase.from('quote_reminders').select('*, profiles(nom)').eq('quote_id', quote.id).order('remind_at', { ascending: true }),
      supabase.from('email_templates').select('*').eq('actif', true).order('delai_jours'),
    ]);
    setNotes(notesData || []);
    setReminders(remindersData || []);
    setTemplates(templatesData || []);
    setLoading(false);
  }, [quote.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('quote_notes').insert({
      quote_id: quote.id, user_id: user.id, content: newNote.trim(),
    });
    if (!error) { setNewNote(''); await fetchData(); }
    setSaving(false);
  };

  const handleDeleteNote = async (noteId) => {
    await supabase.from('quote_notes').delete().eq('id', noteId);
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  // ── Rappels ───────────────────────────────────────────────────────────────
  const handleAddReminder = async () => {
    if (!newReminderDate || !newReminderMsg.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('quote_reminders').insert({
      quote_id: quote.id, user_id: user.id,
      remind_at: newReminderDate, message: newReminderMsg.trim(), statut: 'pending',
    });
    if (!error) {
      setNewReminderDate(''); setNewReminderMsg('');
      await fetchData();
      if (onReminderChange) onReminderChange();
    }
    setSaving(false);
  };

  const handleReminderDone = async (reminderId) => {
    await supabase.from('quote_reminders').update({ statut: 'done' }).eq('id', reminderId);
    setReminders(prev => prev.map(r => r.id === reminderId ? { ...r, statut: 'done' } : r));
    if (onReminderChange) onReminderChange();
  };

  const handleSnooze = async (reminder) => {
    const newDate = new Date(reminder.remind_at);
    newDate.setDate(newDate.getDate() + 3);
    const newDateStr = newDate.toISOString().split('T')[0];
    await supabase.from('quote_reminders').update({ remind_at: newDateStr, statut: 'pending' }).eq('id', reminder.id);
    setReminders(prev => prev.map(r => r.id === reminder.id ? { ...r, remind_at: newDateStr, statut: 'pending' } : r));
    if (onReminderChange) onReminderChange();
  };

  const handleDeleteReminder = async (reminderId) => {
    await supabase.from('quote_reminders').delete().eq('id', reminderId);
    setReminders(prev => prev.filter(r => r.id !== reminderId));
    if (onReminderChange) onReminderChange();
  };

  // ── Ouvrir prévisualisation relance ───────────────────────────────────────
  const handleOuvrirRelance = (reminder) => {
    const clientEmail = quote?.client_info?.email || '';
    if (!clientEmail) {
      alert('Email client manquant — vérifiez les informations client du devis.');
      return;
    }
    const template = getMeilleureRelance();
    const corps = appliquerTemplate(template);
    const sujet = appliquerSujet(template);
    setPreviewModal({
      reminderId: reminder.id,
      subject: sujet,
      body: corps,
      to: clientEmail,
      template,
    });
  };

  // ── Envoyer la relance depuis la prévisualisation ─────────────────────────
  const handleEnvoyerRelance = async () => {
    if (!previewModal) return;
    const { reminderId, to, body, subject } = previewModal;
    setSendingRelance(reminderId);
    setPreviewModal(null);

    const fromName = profile?.nom || 'TAKOUSSANE ENERGY';
    const fromEmail = profile?.email || user?.email || 'contact@takoussane.com';
    const cTitle = quote?.client_info?.attentionTitle ? quote.client_info.attentionTitle + ' ' : '';
    const cName = (cTitle + (quote?.client_info?.attentionName || quote?.client_info?.clientName || quote?.client_info?.name || '')).trim();

    try {
      const result = await sendReminderEmail({
        to,
        cc: ['contact@takoussane.com', ...(quote?.client_info?.emailsCc || []).filter(e => e !== 'contact@takoussane.com')],
        from_name: fromName.trim(),
        from_email: fromEmail,
        client_name: cName,
        quote_number: quote?.quote_number || '',
        message: body,
        total: quote?.total_with_vat
          ? new Intl.NumberFormat('fr-FR').format(Math.round(quote.total_with_vat))
          : '',
        fonction: profile?.fonction || '',
        mobile: profile?.mobile || '',
      });
      if (result.success) {
        alert('✅ Email de relance envoyé à ' + to);
      } else {
        alert('Erreur : ' + (result.error || 'Échec envoi'));
      }
    } catch (e) {
      alert('Erreur envoi : ' + e.message);
    }
    setSendingRelance(null);
  };

  const pendingReminders = reminders.filter(r => r.statut === 'pending');
  const doneReminders = reminders.filter(r => r.statut === 'done');
  const jours = joursDepuisDevis();
  const templateAdapte = getMeilleureRelance();

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', zIndex: 60 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <motion.div
          initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          style={{ width: '420px', height: '100vh', background: 'white',
            display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.18)' }}>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
            padding: '20px 24px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
                📝 Suivi — {quote.quote_number}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '2px' }}>
                {clientName}
                {jours !== null && (
                  <span style={{ marginLeft: '8px', background: 'rgba(255,255,255,0.2)',
                    padding: '1px 8px', borderRadius: '999px', fontSize: '11px' }}>
                    Devis envoyé il y a {jours} j
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
              style={{ background: 'transparent', border: 'none', color: 'white',
                cursor: 'pointer', borderRadius: '6px', padding: '6px', transition: 'background 0.15s',
                display: 'flex', alignItems: 'center' }}>
              <X size={20} />
            </button>
          </div>

          {/* Bandeau relance suggérée */}
          {jours !== null && templateAdapte && quote.statut === 'en_cours' && (
            <div style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a',
              padding: '10px 16px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: '8px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={13} color="#f59e0b" />
                <span style={{ fontSize: '12px', color: '#92400e', fontWeight: 600 }}>
                  Relance J+{templateAdapte.delai_jours} suggérée
                </span>
                <Tooltip text={`Ce devis a été envoyé il y a ${jours} jours.\nLe modèle J+${templateAdapte.delai_jours} est le plus adapté.\nCliquez sur "Relancer" dans un rappel pour envoyer.`}>
                  <Info size={12} color="#f59e0b" style={{ cursor: 'help' }} />
                </Tooltip>
              </div>
              <span style={{ fontSize: '11px', color: '#92400e' }}>
                {jours} j écoulés
              </span>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', flexShrink: 0 }}>
            {[
              { id: 'notes', label: '📝 Notes', count: notes.length },
              { id: 'reminders', label: '🔔 Rappels', count: pendingReminders.length,
                alert: pendingReminders.some(r => isOverdue(r.remind_at) || isDueToday(r.remind_at)) },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ flex: 1, padding: '14px 16px', border: 'none', cursor: 'pointer',
                  fontSize: '13px', fontWeight: tab === t.id ? 700 : 500,
                  background: tab === t.id ? '#eff6ff' : 'white',
                  color: tab === t.id ? '#1e40af' : '#6b7280',
                  borderBottom: tab === t.id ? '2px solid #1e40af' : '2px solid transparent',
                  marginBottom: '-2px', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                {t.label}
                {t.count > 0 && (
                  <span style={{ background: t.alert ? '#ef4444' : (tab === t.id ? '#1e40af' : '#9ca3af'),
                    color: 'white', borderRadius: '999px', padding: '1px 7px',
                    fontSize: '11px', fontWeight: 700 }}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Contenu scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '40px' }}>Chargement...</div>
            ) : tab === 'notes' ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                    placeholder="Ex : Client intéressé, attend financement BHS. Rappel dans 15 jours..."
                    rows={3}
                    onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') handleAddNote(); }}
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px',
                      padding: '10px 12px', fontSize: '13px', outline: 'none',
                      resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                      transition: 'border-color 0.15s' }}
                    onFocus={e => e.target.style.borderColor='#3b82f6'}
                    onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>Ctrl+Entrée pour envoyer</span>
                    <button onClick={handleAddNote} disabled={!newNote.trim() || saving}
                      onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background='#1e3a8a'; }}
                      onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background='#1e40af'; }}
                      style={{ background: newNote.trim() ? '#1e40af' : '#e5e7eb',
                        color: newNote.trim() ? 'white' : '#9ca3af',
                        border: 'none', borderRadius: '6px', padding: '7px 16px',
                        fontSize: '13px', fontWeight: 600, cursor: newNote.trim() ? 'pointer' : 'default',
                        transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <Plus size={13} /> Ajouter
                    </button>
                  </div>
                </div>

                {notes.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '30px' }}>
                    <StickyNote size={36} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px' }}>Aucune note pour ce devis</div>
                    <div style={{ fontSize: '11px', marginTop: '6px', color: '#d1d5db' }}>
                      Notez ici tout élément utile : retours client, objections, prochaines étapes...
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <AnimatePresence>
                      {notes.map(note => (
                        <motion.div key={note.id}
                          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, height: 0 }}
                          style={{ background: '#fffbeb', border: '1px solid #fde68a',
                            borderRadius: '10px', padding: '12px 14px', position: 'relative' }}>
                          <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                            {note.content}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {note.profiles?.nom || 'Moi'} · {fmtDateTime(note.created_at)}
                            </div>
                            {(note.user_id === user?.id || profile?.role === 'manager') && (
                              <button onClick={() => handleDeleteNote(note.id)}
                                onMouseEnter={e => e.currentTarget.style.color='#ef4444'}
                                onMouseLeave={e => e.currentTarget.style.color='#d1d5db'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#d1d5db', transition: 'color 0.15s', padding: '2px',
                                  display: 'flex', alignItems: 'center' }}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Saisie nouveau rappel */}
                <div style={{ marginBottom: '20px', background: '#f8fafc', borderRadius: '10px', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '12px', fontWeight: 700, color: '#374151',
                    marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Nouveau rappel
                    <Tooltip text={"Posez un rappel pour ne pas oublier de recontacter ce prospect.\nVous recevrez une alerte le jour J dans votre dashboard."}>
                      <Info size={12} color="#9ca3af" style={{ cursor: 'help' }} />
                    </Tooltip>
                  </div>
                  <input type="date" value={newReminderDate} min={today()}
                    onChange={e => setNewReminderDate(e.target.value)}
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '6px',
                      padding: '8px 10px', fontSize: '13px', outline: 'none',
                      marginBottom: '8px', boxSizing: 'border-box' }} />
                  <input type="text" value={newReminderMsg}
                    onChange={e => setNewReminderMsg(e.target.value)}
                    placeholder="Ex : Rappeler M. Ndiaye — financement obtenu ?"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddReminder(); }}
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '6px',
                      padding: '8px 10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />

                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                    {[
                      { label: '+3j', days: 3 }, { label: '+7j', days: 7 },
                      { label: '+15j', days: 15 }, { label: '+1 mois', days: 30 },
                      { label: '+2 mois', days: 60 }, { label: '+3 mois', days: 90 },
                    ].map(({ label, days }) => {
                      const d = new Date();
                      d.setDate(d.getDate() + days);
                      const dateStr = d.toISOString().split('T')[0];
                      const isSelected = newReminderDate === dateStr;
                      return (
                        <button key={label} onClick={() => setNewReminderDate(dateStr)}
                          onMouseEnter={e => { e.currentTarget.style.background='#dbeafe'; e.currentTarget.style.borderColor='#93c5fd'; e.currentTarget.style.color='#1e3a8a'; }}
                          onMouseLeave={e => { e.currentTarget.style.background=isSelected?'#dbeafe':'white'; e.currentTarget.style.borderColor=isSelected?'#93c5fd':'#e5e7eb'; e.currentTarget.style.color=isSelected?'#1e40af':'#6b7280'; }}
                          style={{ background: isSelected ? '#dbeafe' : 'white',
                            color: isSelected ? '#1e40af' : '#6b7280',
                            border: `1px solid ${isSelected ? '#93c5fd' : '#e5e7eb'}`,
                            borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                            cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <button onClick={handleAddReminder}
                    disabled={!newReminderDate || !newReminderMsg.trim() || saving}
                    onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background='#1e3a8a'; }}
                    onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background='#1e40af'; }}
                    style={{ width: '100%', marginTop: '12px',
                      background: (newReminderDate && newReminderMsg.trim()) ? '#1e40af' : '#e5e7eb',
                      color: (newReminderDate && newReminderMsg.trim()) ? 'white' : '#9ca3af',
                      border: 'none', borderRadius: '6px', padding: '9px',
                      fontSize: '13px', fontWeight: 600,
                      cursor: (newReminderDate && newReminderMsg.trim()) ? 'pointer' : 'default',
                      transition: 'all 0.15s', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '5px' }}>
                    <Bell size={13} /> Poser le rappel
                  </button>
                </div>

                {pendingReminders.length === 0 && doneReminders.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '20px' }}>
                    <Bell size={36} style={{ opacity: 0.3, marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px' }}>Aucun rappel posé</div>
                    <div style={{ fontSize: '11px', marginTop: '6px', color: '#d1d5db' }}>
                      Posez un rappel pour ne jamais oublier de relancer un prospect
                    </div>
                  </div>
                ) : (
                  <>
                    {pendingReminders.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151',
                          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                          En attente
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <AnimatePresence>
                            {pendingReminders.map(r => {
                              const overdue = isOverdue(r.remind_at);
                              const dueToday = isDueToday(r.remind_at);
                              const bgColor = overdue ? '#fef2f2' : dueToday ? '#fefce8' : '#f0f9ff';
                              const borderColor = overdue ? '#fca5a5' : dueToday ? '#fcd34d' : '#bae6fd';
                              const badgeBg = overdue ? '#ef4444' : dueToday ? '#f59e0b' : '#3b82f6';
                              const badgeLabel = overdue ? '⚠️ En retard' : dueToday ? "🔔 Aujourd'hui" : `📅 ${fmtDate(r.remind_at)}`;
                              const isSending = sendingRelance === r.id;
                              return (
                                <motion.div key={r.id}
                                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, height: 0 }}
                                  style={{ background: bgColor, border: `1px solid ${borderColor}`,
                                    borderRadius: '10px', padding: '12px 14px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'flex-start', marginBottom: '6px' }}>
                                    <span style={{ background: badgeBg, color: 'white',
                                      borderRadius: '6px', padding: '2px 8px',
                                      fontSize: '11px', fontWeight: 700 }}>
                                      {badgeLabel}
                                    </span>
                                    <button onClick={() => handleDeleteReminder(r.id)}
                                      onMouseEnter={e => e.currentTarget.style.color='#ef4444'}
                                      onMouseLeave={e => e.currentTarget.style.color='#d1d5db'}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#d1d5db', transition: 'color 0.15s', padding: '2px',
                                        display: 'flex', alignItems: 'center' }}>
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#1f2937', marginBottom: '8px' }}>
                                    {r.message}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                                    {r.profiles?.nom || 'Moi'} · posé le {fmtDateTime(r.created_at)}
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <button onClick={() => handleReminderDone(r.id)}
                                      onMouseEnter={e => { e.currentTarget.style.background='#dcfce7'; e.currentTarget.style.borderColor='#86efac'; e.currentTarget.style.color='#065f46'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background='white'; e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.color='#10b981'; }}
                                      style={{ flex: 1, background: 'white', color: '#10b981',
                                        border: '1px solid #e5e7eb', borderRadius: '6px',
                                        padding: '5px', fontSize: '11px', fontWeight: 600,
                                        cursor: 'pointer', transition: 'all 0.15s',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                      <Check size={12} /> Fait
                                    </button>
                                    <button onClick={() => handleSnooze(r)}
                                      onMouseEnter={e => { e.currentTarget.style.background='#fef9c3'; e.currentTarget.style.borderColor='#fcd34d'; e.currentTarget.style.color='#854d0e'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background='white'; e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.color='#6b7280'; }}
                                      style={{ flex: 1, background: 'white', color: '#6b7280',
                                        border: '1px solid #e5e7eb', borderRadius: '6px',
                                        padding: '5px', fontSize: '11px', fontWeight: 600,
                                        cursor: 'pointer', transition: 'all 0.15s',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                      <Clock size={12} /> +3 jours
                                    </button>
                                    {/* Bouton Relancer avec tooltip */}
                                    <Tooltip text={templateAdapte
                                      ? `Envoie le modèle J+${templateAdapte.delai_jours} au client.\nVous pourrez relire le message avant d'envoyer.`
                                      : "Envoie un email de relance au client"}>
                                      <button onClick={() => handleOuvrirRelance(r)}
                                        disabled={isSending}
                                        onMouseEnter={e => { if (!isSending) { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#93c5fd'; e.currentTarget.style.color='#1e40af'; } }}
                                        onMouseLeave={e => { if (!isSending) { e.currentTarget.style.background='white'; e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.color='#6b7280'; } }}
                                        style={{ flex: 1, background: 'white', color: '#6b7280',
                                          border: '1px solid #e5e7eb', borderRadius: '6px',
                                          padding: '5px', fontSize: '11px', fontWeight: 600,
                                          cursor: isSending ? 'default' : 'pointer', transition: 'all 0.15s',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                          opacity: isSending ? 0.6 : 1 }}>
                                        <Mail size={12} /> {isSending ? '...' : 'Relancer'}
                                      </button>
                                    </Tooltip>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>
                        </div>
                      </div>
                    )}

                    {doneReminders.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af',
                          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                          Traités ({doneReminders.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {doneReminders.map(r => (
                            <div key={r.id}
                              style={{ background: '#f9fafb', border: '1px solid #e5e7eb',
                                borderRadius: '8px', padding: '10px 12px',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'line-through' }}>
                                  {r.message}
                                </div>
                                <div style={{ fontSize: '11px', color: '#d1d5db' }}>{fmtDate(r.remind_at)}</div>
                              </div>
                              <button onClick={() => handleDeleteReminder(r.id)}
                                onMouseEnter={e => e.currentTarget.style.color='#ef4444'}
                                onMouseLeave={e => e.currentTarget.style.color='#d1d5db'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#d1d5db', transition: 'color 0.15s', padding: '2px',
                                  display: 'flex', alignItems: 'center' }}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* ── Modal prévisualisation email de relance ── */}
      <AnimatePresence>
        {previewModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 70, padding: '16px' }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px',
                maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

              {/* Header modal */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                    📧 Prévisualisation de la relance
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    Vérifiez le message avant d'envoyer
                  </div>
                </div>
                <button onClick={() => setPreviewModal(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <X size={20} />
                </button>
              </div>

              {/* Corps modal */}
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                {/* Destinataire */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Destinataire
                  </div>
                  <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '8px',
                    fontSize: '13px', color: '#374151', border: '1px solid #e5e7eb' }}>
                    📧 {previewModal.to}
                  </div>
                </div>

                {/* Sujet */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Objet
                  </div>
                  <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '8px',
                    fontSize: '13px', color: '#374151', border: '1px solid #e5e7eb' }}>
                    {previewModal.subject}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px',
                    display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Message
                    <Tooltip text="Vous pouvez modifier le message avant d'envoyer.\nLe modèle de base est configuré par le manager.">
                      <Info size={12} color="#9ca3af" style={{ cursor: 'help' }} />
                    </Tooltip>
                  </div>
                  <textarea
                    value={previewModal.body}
                    onChange={e => setPreviewModal(prev => ({ ...prev, body: e.target.value }))}
                    rows={10}
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px',
                      padding: '10px 12px', fontSize: '13px', outline: 'none',
                      resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                      lineHeight: '1.6' }}
                    onFocus={e => e.target.style.borderColor='#3b82f6'}
                    onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                </div>

                {/* Info modèle utilisé */}
                {previewModal.template && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 12px', background: '#eff6ff', borderRadius: '8px',
                    fontSize: '12px', color: '#1e40af' }}>
                    <Info size={13} />
                    Modèle J+{previewModal.template.delai_jours} appliqué automatiquement selon la date du devis
                  </div>
                )}
              </div>

              {/* Footer modal */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setPreviewModal(null)}
                  style={{ padding: '8px 18px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                    borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleEnvoyerRelance}
                  onMouseEnter={e => e.currentTarget.style.background='#1e3a8a'}
                  onMouseLeave={e => e.currentTarget.style.background='#1e40af'}
                  style={{ padding: '8px 20px', background: '#1e40af', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Mail size={14} /> Envoyer la relance
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuoteNotes;